/**
 * Central registry for Mission sub-KPI types.
 *
 * Each type registers itself here. Lookup by `type_key` is used at scoring
 * time (engine dispatches to the registered type's `score` function) and at
 * configuration time (setup wizard reads the type's `config_schema`).
 *
 * Adding a new type:
 *   1. Implement the MissionSubKpiType interface in a new file under missionTypes/
 *   2. Import and add it to the REGISTRY object below
 *   3. (No other changes required — no union types to edit, no validators to update)
 *
 * See: DEV-814 KPI Engine Refinement, Phase 2.
 */

import type { MissionSubKpiType } from './types';
import { reductionType } from './reduction';
import { binaryType } from './binary';
import { countType } from './count';
import { improvementType } from './improvement';
import { rateType } from './rate';
import { milestoneSetType } from './milestoneSet';
import { incidentCountType } from './incidentCount';

/* ------------------------------------------------------------------------- */
/*  Registry                                                                 */
/* ------------------------------------------------------------------------- */

const REGISTRY: Record<string, MissionSubKpiType> = {
  // Order here drives the display order in the wizard's type picker.
  [binaryType.type_key]: binaryType,
  [countType.type_key]: countType,
  [improvementType.type_key]: improvementType,
  [reductionType.type_key]: reductionType,
  [rateType.type_key]: rateType,
  [milestoneSetType.type_key]: milestoneSetType,
  // HSE Incident Tracker (DEV-822) — special-cased renderer (square cards
  // above the Mission rows; floor-cap on Mission summary). Same plug-in
  // pattern as the others; the UI layer reads `type_key === 'incident_count'`
  // to swap the renderer.
  [incidentCountType.type_key]: incidentCountType,
};

/* ------------------------------------------------------------------------- */
/*  Lookup API                                                               */
/* ------------------------------------------------------------------------- */

/**
 * Get a registered Mission sub-KPI type by its `type_key`.
 * Returns null if the key is not registered.
 */
export function getMissionSubKpiType(
  typeKey: string,
): MissionSubKpiType | null {
  return REGISTRY[typeKey] ?? null;
}

/**
 * List all registered Mission sub-KPI types.
 * Order is the order of registration in the REGISTRY object.
 */
export function listMissionSubKpiTypes(): MissionSubKpiType[] {
  return Object.values(REGISTRY);
}

/**
 * Register a Mission sub-KPI type.
 * Primarily used in tests; production registration happens at module load
 * via the static REGISTRY object above.
 */
export function registerMissionSubKpiType(type: MissionSubKpiType): void {
  REGISTRY[type.type_key] = type;
}

/**
 * Unregister a Mission sub-KPI type. Test-only utility for isolating tests
 * that register temporary types.
 */
export function unregisterMissionSubKpiType(typeKey: string): void {
  delete REGISTRY[typeKey];
}
